import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  MapPin,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile + 2 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons à domicile à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne gaspillez pas votre énergie à transformer votre voiture en
            utilitaire pour transporter des colis encombrants et poussiéreux.
            Notre service prend en charge l&apos;intégralité du flux routier
            pour déposer votre commande au cœur de {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Circuit fermé propriétaire",
              body: "Nous bannissons la sous-traitance et les réseaux de messagerie industriels. Nos propres techniciens pilotent le camion et manipulent vos produits, éliminant ainsi les écrasements avant la livraison.",
              Icon: Truck,
            },
            {
              title: "Garantie thermique et structurelle",
              body: "Nos véhicules hermétiques préservent les liaisons moléculaires du carton contre l'humidité extérieure. Vous ouvrez des colis sains, rigides et dotés de leur pouvoir de friction maximal.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Kits prêts à l'emploi : force des solutions unifiées ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Solutions unifiées
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi adopter nos kits prêts à l&apos;emploi&nbsp;? La force
            des solutions unifiées
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Cessez de deviner ou d&apos;estimer vos besoins au hasard. Nos
            ingénieurs logistiques ont fusionné l&apos;ensemble des
            indispensables au sein de dotations globales prêtes à
            l&apos;action.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Cohésion technique",
              body: "Plus qu'un simple empilement de boîtes, nos solutions regroupent les formats complémentaires (modules denses pour le papier, volumes légers pour les textiles) associés au volume exact de rubans de scellage et de matelas d'air requis.",
              Icon: Layers,
            },
            {
              title: "Bouclier budgétaire",
              body: "Le regroupement de vos fournitures au sein d'une seule référence déclenche une réduction tarifaire de gros, vous évitant de payer la taxe du sur-mesure ou de subir les frais de livraison répétés pour des oublis de dernière minute.",
              Icon: Tag,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-gennevilliers-cta-packs"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les kits déménagement
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Gamme biosourcée (accent vert) ======= */}
    <section className="py-14 md:py-20 bg-[#F0FAF3] border-y border-[#BCE5C9]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#16A34A]/10 border border-[#16A34A]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#15803D] mb-4">
            <Recycle className="h-3.5 w-3.5" strokeWidth={2.6} />
            Empreinte neutre
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            La performance de notre gamme biosourcée
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous prouvons qu&apos;il est possible de sécuriser un transfert
            lourd tout en respectant les cycles de la nature. Découvrez nos
            emballages à empreinte neutre.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Ressources circulaires",
              body: "Nos modules proviennent exclusivement de la revalorisation de matières végétales. Ils sont intégralement biodégradables et pensés pour réintégrer le circuit de recyclage après leur utilisation.",
              Icon: Recycle,
            },
            {
              title: "Densité mécanique préservée",
              body: "Grâce à un procédé de pressage à haute densité des fibres, nos cartons éco-conçus affichent une résistance au gerbage (l'empilement vertical) supérieure aux exigences de la norme européenne, bloquant toute tentative d'affaissement dans le véhicule.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#BCE5C9] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#16A34A] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-reconditionnes"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-[14px] transition-colors"
            data-testid="city-gennevilliers-cta-bio"
          >
            <Recycle className="h-4 w-4" strokeWidth={2.6} />
            Voir nos cartons éco-responsables
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-gennevilliers-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
